import React, { useState, useEffect } from 'react';
import { subscriptionsAPI, accountsAPI, categoriesAPI, recurringBillsAPI } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';
import { parseCalendarDay } from '../utils/localDate';

const Subscriptions = ({ selectedBusiness }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [stats, setStats] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    loadData();
  }, [selectedBusiness]);

  const loadData = async () => {
    try {
      const params = selectedBusiness ? { business_id: selectedBusiness } : {};
      
      const [subsRes, accRes, catRes, statsRes, billsRes] = await Promise.all([
        subscriptionsAPI.getAll(params),
        accountsAPI.getAll(params),
        categoriesAPI.getAll(),
        subscriptionsAPI.getStats(params),
        selectedBusiness ? recurringBillsAPI.getAll(params) : Promise.resolve({ data: [] }),
      ]);
      
      setSubscriptions(subsRes.data);
      setAccounts(accRes.data);
      setCategories(catRes.data);
      setStats(statsRes.data);
      const listed = new Set(
        (subsRes.data || []).map((sub) => `${sub.business_id}:${String(sub.service_name || '').trim().toLowerCase()}`)
      );
      setSuggestions(
        (billsRes.data || []).filter((bill) => {
          const name = String(bill.bill_name || '').trim().toLowerCase();
          if (!name) return false;
          return !listed.has(`${bill.business_id}:${name}`);
        })
      );
    } catch (error) {
      console.error('Failed to load subscriptions:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    try {
      if (editingSubscription) {
        await subscriptionsAPI.update(editingSubscription.id, data);
      } else {
        await subscriptionsAPI.create(data);
      }
      setShowModal(false);
      setEditingSubscription(null);
      loadData();
    } catch (error) {
      console.error('Failed to save subscription:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  };

  const addSuggestion = async (bill) => {
    await subscriptionsAPI.create({
      business_id: bill.business_id,
      account_id: bill.account_id || null,
      service_name: bill.bill_name,
      amount: bill.amount,
      billing_frequency: bill.frequency || 'monthly',
      billing_day: bill.due_day || null,
      next_billing_date: bill.next_due_date || null,
      category_id: bill.category_id || null,
      importance_level: 'medium'
    });
    loadData();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this subscription?')) {
      try {
        await subscriptionsAPI.delete(id);
        loadData();
      } catch (error) {
        console.error('Failed to delete subscription:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  return (
    <ErrorBoundary section="Subscriptions">
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Subscriptions</h2>
          <p className="text-gray-600 mt-1">Manage your recurring expenses</p>
        </div>
        <button
          onClick={() => { setShowModal(true); setEditingSubscription(null); }}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          + Add Subscription
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Total Subscriptions</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total_subscriptions}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Estimated monthly</p>
            <p className="text-3xl font-bold text-blue-600">{formatCurrency(stats.total_monthly)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Estimated quarterly</p>
            <p className="text-3xl font-bold text-purple-600">{formatCurrency(stats.total_quarterly)}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600">Estimated annual</p>
            <p className="text-3xl font-bold text-red-600">{formatCurrency(stats.total_annual_cost)}</p>
          </div>
        </div>
      )}

      {selectedBusiness && suggestions.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Suggested from recurring bills</h3>
          <p className="text-sm text-gray-600 mb-4">These bills for this location are not on the service list yet.</p>
          <ul className="divide-y divide-gray-200">
            {suggestions.map((bill) => (
              <li key={bill.id} className="py-3 flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">{bill.bill_name}</p>
                  <p className="text-xs text-gray-500 capitalize">{bill.frequency} · {formatCurrency(bill.amount)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => addSuggestion(bill)}
                  className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                >
                  Add to service list
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Subscriptions List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Business</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Importance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Frequency</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Estimated annual</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Next Billing</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {subscriptions.map((sub) => (
              <tr key={sub.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{sub.service_name}</p>
                    {sub.website_url && (
                      <a href={sub.website_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                        {sub.website_url}
                      </a>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">{sub.business_name}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    sub.importance_level === 'critical' ? 'bg-red-100 text-red-800' :
                    sub.importance_level === 'high' ? 'bg-orange-100 text-orange-800' :
                    sub.importance_level === 'medium' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {sub.importance_level || 'medium'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-600 capitalize">{sub.billing_frequency}</td>
                <td className="px-6 py-4 text-sm text-right font-semibold">{formatCurrency(sub.amount)}</td>
                <td className="px-6 py-4 text-sm text-right font-semibold text-red-600">{formatCurrency(sub.annual_cost)}</td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  {sub.next_billing_date ? parseCalendarDay(sub.next_billing_date).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-6 py-4 text-center">
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    sub.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {sub.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right text-sm">
                  <button
                    onClick={() => { setEditingSubscription(sub); setShowModal(true); }}
                    className="text-blue-600 hover:text-blue-800 mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(sub.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{editingSubscription ? 'Edit' : 'Add'} Subscription</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="hidden" name="business_id" value={selectedBusiness || editingSubscription?.business_id || ''} />
              
              <div>
                <label className="block text-sm font-medium mb-1">Service Name</label>
                <input
                  type="text"
                  name="service_name"
                  defaultValue={editingSubscription?.service_name || ''}
                  required
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Website URL</label>
                <input
                  type="url"
                  name="website_url"
                  defaultValue={editingSubscription?.website_url || ''}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Amount</label>
                <input
                  type="number"
                  name="amount"
                  step="0.01"
                  defaultValue={editingSubscription?.amount || ''}
                  required
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Billing Frequency</label>
                <select
                  name="billing_frequency"
                  defaultValue={editingSubscription?.billing_frequency || 'monthly'}
                  required
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Importance Level</label>
                <select
                  name="importance_level"
                  defaultValue={editingSubscription?.importance_level || 'medium'}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="critical">🔴 Critical - Never Cancel</option>
                  <option value="high">🟠 High - Important</option>
                  <option value="medium">🔵 Medium - Standard</option>
                  <option value="low">⚪ Low - Cancel First</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Use this to prioritize which subscriptions to cancel when cutting costs
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Next Billing Date</label>
                <input
                  type="date"
                  name="next_billing_date"
                  defaultValue={editingSubscription?.next_billing_date?.split('T')[0] || ''}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Account</label>
                <select
                  name="account_id"
                  defaultValue={editingSubscription?.account_id || ''}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select Account</option>
                  {accounts.map((acc) => (
                    <option key={acc.id} value={acc.id}>{acc.account_name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select
                  name="category_id"
                  defaultValue={editingSubscription?.category_id || ''}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Select Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_active"
                    defaultChecked={editingSubscription?.is_active !== false}
                    className="mr-2"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingSubscription(null); }}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingSubscription ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </ErrorBoundary>
  );
};

export default Subscriptions;
